/* =========================================================================
   femilism-cart-subscribe.js
   -------------------------------------------------------------------------
   WHY THIS EXISTS

   Sugar's cart drawer can DISPLAY a subscription line but it cannot CREATE
   one. blocks/sugar-cart-items.liquid has exactly one subscription setting,
   `subscription_label`, and all it does is choose how to label a line that
   ALREADY carries a selling plan. Nothing in the drawer converts a one-time
   line into a subscription -- `cart-toggles` adds a DIFFERENT product as an
   upsell, which is not the same thing.

   This file adds the missing control. For every one-time line whose variant
   has a selling plan available, it injects a "Subscribe & save" button.

   HOW THE SWAP WORKS
   Shopify's Cart AJAX API accepts `selling_plan` on /cart/change.js, so the
   upgrade is ONE atomic request, not a remove-then-re-add. Two constraints
   from Shopify's docs, both load-bearing:
     * when sending `selling_plan`, the line MUST be identified by `line`
       (its 1-based position). A line-item key is rejected.
     * `quantity` must always be sent, or Shopify assumes 1 and silently
       collapses a multi-unit line down to a single unit.
   The line number is resolved from /cart.js by matching the line-item key,
   never read off the DOM. Sugar's own code comments warn that positional
   indexes shift when a variant splits across several lines.

   HOW THE DRAWER REPAINTS
   Sugar re-renders on the pubsub cartUpdate event. Both <sugar-cart-drawer>
   and <sugar-cart-items> deliberately IGNORE events whose `source` is their
   own, so publishing under our own source name makes both re-fetch and
   repaint. This mirrors what Sugar's <sugar-cart-progress> already does
   after it auto-adds a gift.

   DELIBERATE NON-COLLISIONS
   * <sugar-cart-items> binds a WINDOW CAPTURE click listener that calls
     stopImmediatePropagation() on .sugar-cart-item__remove and
     .sugar-cart-item__quantity-btn. Our button uses neither class, so our
     clicks are never swallowed.
   * The stylesheet is injected into <head>, never into the drawer. Sugar
     renders inline <style> tags inside its blocks, and anything that
     restyles the drawer's children can turn those into visible CSS text.
   * Re-injection is driven by a MutationObserver rather than by the pubsub
     event, because the drawer swaps .sugar-cart-drawer__items innerHTML
     from several different code paths.

   To revert: delete this file and its <script> tag in layout/theme.liquid.
   ========================================================================= */
(function () {
  'use strict';

  var MARKER = 'data-femilism-sub';
  var STYLE_ID = 'femilism-cart-subscribe-styles';
  var SOURCE = 'femilism-cart-subscribe';
  var UNITS = ['day', 'week', 'month', 'year'];

  var productCache = {};
  var scanTimer = null;
  var busy = false;

  function shopRoot() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  /* ---------------------------------------------------------------------
     Frequency parsing. Same hyphen problem the PDP shim exists to solve:
     Recharge writes "1-month", most themes expect "1 month". Split on any
     of hyphen / space / underscore so both survive.
     --------------------------------------------------------------------- */
  function parseFrequency(value) {
    if (!value) return null;
    var parts = String(value).toLowerCase().split(/[-\s_]+/);
    var number = '';
    var unit = '';

    parts.forEach(function (part) {
      var singular = part.replace(/s$/, '');
      if (UNITS.indexOf(singular) !== -1) {
        unit = singular;
      } else if (/^\d+$/.test(part) && part !== '0') {
        number = part;
      }
    });

    if (!unit) return null;
    if (!number) number = '1';
    return { number: number, unit: unit };
  }

  function frequencyLabel(plan) {
    var freq = null;
    (plan.options || []).forEach(function (opt) {
      if (freq) return;
      freq = parseFrequency(typeof opt === 'string' ? opt : opt && opt.value);
    });
    if (!freq) return '';
    if (freq.number === '1') return 'Every ' + freq.unit;
    return 'Every ' + freq.number + ' ' + freq.unit + 's';
  }

  /* ---------------------------------------------------------------------
     Product lookup, cached per handle for the life of the page.
     --------------------------------------------------------------------- */
  function loadProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    productCache[handle] = fetch(shopRoot() + 'products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });

    return productCache[handle];
  }

  /* Find the cheapest available plan for this exact variant. Per-variant
     allocations are authoritative -- a product can carry a plan that a
     given variant is not eligible for. */
  function planForVariant(product, variantId) {
    if (!product || !product.variants) return null;

    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(variantId)) {
        variant = product.variants[i];
        break;
      }
    }
    if (!variant) return null;

    var allocations = variant.selling_plan_allocations || [];
    if (!allocations.length) return null;

    var best = null;
    allocations.forEach(function (alloc) {
      if (!best || alloc.price < best.price) best = alloc;
    });
    if (!best) return null;

    /* Pair the allocation back to its plan so we can read the cadence. */
    var plan = null;
    (product.selling_plan_groups || []).forEach(function (group) {
      (group.selling_plans || []).forEach(function (p) {
        if (String(p.id) === String(best.selling_plan_id)) plan = p;
      });
    });

    var basePrice = typeof variant.price === 'number' ? variant.price : 0;
    var percent = 0;
    if (basePrice > 0 && best.price < basePrice) {
      percent = Math.round(((basePrice - best.price) / basePrice) * 100);
    }

    return {
      id: best.selling_plan_id,
      percent: percent,
      cadence: plan ? frequencyLabel(plan) : ''
    };
  }

  /* ---------------------------------------------------------------------
     Lines Sugar auto-manages (gifts, progress rewards, cart toggles) must
     never get an upgrade button -- the drawer adds and removes them itself.
     Mirrors SugarCartItems.isAutoAddedCartItem.
     --------------------------------------------------------------------- */
  function isAutoAdded(item) {
    var p = (item && item.properties) || {};
    return (
      p._gift_list === 'true' ||
      p._gift_unlock === 'true' ||
      p._cart_toggle === 'true' ||
      p._cart_upsell === 'true' ||
      Object.prototype.hasOwnProperty.call(p, '_offer_gift') ||
      Object.prototype.hasOwnProperty.call(p, '_sugar_progress_goal')
    );
  }

  /* ---------------------------------------------------------------------
     Styles. Injected into <head> so they survive every drawer repaint and
     never sit inside a Sugar block.
     --------------------------------------------------------------------- */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var css = [
      '.femilism-sub{margin-top:10px}',
      '.femilism-sub__btn{display:flex;align-items:center;gap:8px;width:100%;',
      'padding:9px 12px;border:1px solid #EAD8D0;background:#FAF7F3;color:#650024;',
      'font-family:var(--font-body-family,inherit);text-align:left;cursor:pointer;',
      'border-radius:0;line-height:1.25;transition:background .15s ease,border-color .15s ease}',
      '.femilism-sub__btn:hover{background:#F3EDE7;border-color:#650024}',
      '.femilism-sub__btn[disabled]{opacity:.55;cursor:default}',
      '.femilism-sub__mark{flex:0 0 auto;width:16px;height:16px;border:1px solid #C99845;',
      'border-radius:50%;position:relative}',
      '.femilism-sub__mark:after{content:"";position:absolute;inset:3px;border-radius:50%;',
      'background:#C99845;opacity:0;transition:opacity .15s ease}',
      '.femilism-sub__btn:hover .femilism-sub__mark:after{opacity:1}',
      '.femilism-sub__text{display:flex;flex-direction:column;gap:1px;min-width:0}',
      '.femilism-sub__label{font-size:1.2rem;font-weight:600;letter-spacing:.01em}',
      '.femilism-sub__note{font-size:1.05rem;color:#A58D82;font-weight:400}',
      '.femilism-sub__btn.is-working .femilism-sub__label{opacity:.6}'
    ].join('');

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }

  function buildControl(line, plan) {
    var wrap = document.createElement('div');
    wrap.className = 'femilism-sub';
    wrap.setAttribute(MARKER, '1');

    var label = plan.percent > 0
      ? 'Subscribe & save ' + plan.percent + '%'
      : 'Subscribe & save';

    var note = plan.cadence
      ? plan.cadence + ' \u00b7 cancel anytime'
      : 'Cancel anytime';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'femilism-sub__btn';
    btn.dataset.line = String(line.line);
    btn.dataset.plan = String(plan.id);
    btn.dataset.qty = String(line.quantity);

    var mark = document.createElement('span');
    mark.className = 'femilism-sub__mark';

    var text = document.createElement('span');
    text.className = 'femilism-sub__text';

    var labelEl = document.createElement('span');
    labelEl.className = 'femilism-sub__label';
    labelEl.textContent = label;

    var noteEl = document.createElement('span');
    noteEl.className = 'femilism-sub__note';
    noteEl.textContent = note;

    text.appendChild(labelEl);
    text.appendChild(noteEl);
    btn.appendChild(mark);
    btn.appendChild(text);
    wrap.appendChild(btn);

    return wrap;
  }

  /* ---------------------------------------------------------------------
     Locate a line's rendered row. Match on the line-item KEY, which the
     theme prints on both the quantity input and the remove button. Fall
     back to data-line-index only if neither is present (quantity selectors
     are suppressed on some auto-added rows).
     --------------------------------------------------------------------- */
  function rowForLine(scope, item, lineNumber) {
    var keyed = scope.querySelector(
      '.sugar-cart-item__quantity-input[data-key="' + item.key + '"],' +
      '.sugar-cart-item__remove[data-key="' + item.key + '"]'
    );
    if (keyed && keyed.closest) return keyed.closest('.sugar-cart-item');
    return scope.querySelector('.sugar-cart-item[data-line-index="' + lineNumber + '"]');
  }

  function scan() {
    var scope = document.querySelector('sugar-cart-items');
    if (!scope) return;

    fetch(shopRoot() + 'cart.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;

        cart.items.forEach(function (item, index) {
          var lineNumber = index + 1;

          if (item.selling_plan_allocation) return;
          if (isAutoAdded(item)) return;

          var row = rowForLine(scope, item, lineNumber);
          if (!row || row.querySelector('[' + MARKER + ']')) return;

          var details = row.querySelector('.sugar-cart-item__details');
          if (!details) return;

          loadProduct(item.handle).then(function (product) {
            var plan = planForVariant(product, item.variant_id);
            if (!plan) return;

            /* The drawer may have repainted while we were fetching. */
            if (!row.isConnected || row.querySelector('[' + MARKER + ']')) return;

            details.appendChild(buildControl(
              { line: lineNumber, quantity: item.quantity },
              plan
            ));
          });
        });
      })
      .catch(function () { /* leave the drawer untouched rather than half-built */ });
  }

  function scheduleScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(function () {
      scanTimer = null;
      scan();
    }, 80);
  }

  /* ---------------------------------------------------------------------
     The upgrade itself.
     --------------------------------------------------------------------- */
  function upgrade(btn) {
    if (busy) return;
    busy = true;

    btn.disabled = true;
    btn.classList.add('is-working');

    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

    fetch(changeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        line: parseInt(btn.dataset.line, 10),
        quantity: parseInt(btn.dataset.qty, 10) || 1,
        selling_plan: parseInt(btn.dataset.plan, 10)
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || cart.status) throw new Error('cart change rejected');

        /* Sugar's own components skip cartUpdate events they raised
           themselves, so publishing under our source makes both the drawer
           and the item list re-fetch and repaint. */
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
        } else {
          var drawer = document.querySelector('sugar-cart-drawer');
          if (drawer && typeof drawer.onCartUpdate === 'function') drawer.onCartUpdate({});
        }

        scheduleScan();
      })
      .catch(function () {
        btn.disabled = false;
        btn.classList.remove('is-working');

        var errors = document.getElementById('SugarCartDrawer-CartErrors');
        if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
      })
      .finally(function () { busy = false; });
  }

  /* ---------------------------------------------------------------------
     Wiring.
     --------------------------------------------------------------------- */
  function init() {
    injectStyles();

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.femilism-sub__btn');
      if (!btn) return;
      e.preventDefault();
      upgrade(btn);
    });

    /* The drawer replaces .sugar-cart-drawer__items innerHTML from several
       code paths, so watch the DOM rather than trusting any one event.
       scan() is idempotent, so our own insertions re-enter harmlessly. */
    var drawer = document.querySelector('sugar-cart-drawer');
    if (drawer) {
      new MutationObserver(scheduleScan).observe(drawer, {
        childList: true,
        subtree: true
      });
    }

    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
